import { useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";

import { useQueryClient } from "@tanstack/react-query";

import {
  useGetAuthStatus,
  useGetSsoProviderMutation,
  useLoginMutation,
  useLogoutMutation
} from "@/api/auth/useAuthAPI";
import { useGetUserProfileAPI } from "@/api/userProfile/useUserProfileAPI";
import { ERROR_LOGIN_MAPPING } from "@/components/login/data";
import NotificationContent from "@/components/notification/notificationContent";
import { EMAIL_OF_INTERNAL_TESTER } from "@/constants/user";
import { useSso } from "@/context/SsoProvider";
import useToast, { EToastType } from "@/hooks/ui/useToast";
import { resetStore, RootState } from "@/store";
import { setAuth } from "@/store/slice/authSlice";
import { CustomError } from "@/utils/errors";

import { datadogRum } from "@datadog/browser-rum";

export const useAuth = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const dispatch = useDispatch();
  const { initSso } = useSso();
  const { hasLoggedIn } = useSelector((state: RootState) => state.auth);

  const { isLoading: isAuthenticating, isSuccess: isAuthenticated } =
    useGetAuthStatus({
      enabled: hasLoggedIn
    });

  // TODO: need to revisit usage of this one. for now this is used to set the user in Datadog RUM
  const { data: userProfile } = useGetUserProfileAPI({ enabled: hasLoggedIn });

  useEffect(() => {
    // Set the user in Datadog RUM if hasLoggedIn is true
    if (
      hasLoggedIn &&
      userProfile?.email_address !== EMAIL_OF_INTERNAL_TESTER
    ) {
      datadogRum.setUserProperty("id", userProfile?.email_address);
      datadogRum.setUserProperty("full_name", userProfile?.full_name);
    } else {
      // Clear the user and stop session in Datadog RUM if hasLoggedIn is false or user is internal tester
      datadogRum.stopSession();
      datadogRum.stopSessionReplayRecording();
      datadogRum.clearUser();
    }
  }, [hasLoggedIn, userProfile]);

  const { mutate: logout, isPending: isLoggingOut } = useLogoutMutation({
    onSuccess: () => {
      // Clear React Query cache
      queryClient.clear();
      datadogRum.clearUser();
      toast.dismiss();
      resetStore();
    },
    onError: () => {
      showToast({
        type: EToastType.ERROR,
        message: (
          <NotificationContent
            title="Something Went Wrong"
            description="Sorry, we're experiencing an issue processing your request. Please try again."
          />
        ),
        autoClose: 3000
      });
    }
  });

  const { mutate: login, isPending: isLoggingIn } = useLoginMutation({
    onSuccess: (result) => {
      dispatch(
        setAuth({
          accessToken: result.access_token, // TODO: after cookie is set, we can remove this
          refreshToken: result.refresh_token, // TODO: after cookie is set, we can remove this
          hasLoggedIn: true // new flag to indicate user has logged in. to reduce dependency on refresh token and access token
        })
      );
    },
    onError: (error: { message: CustomError }) => {
      const errorCode = (error?.message?.code ??
        "default") as keyof typeof ERROR_LOGIN_MAPPING;

      const title =
        ERROR_LOGIN_MAPPING[errorCode]?.title ??
        ERROR_LOGIN_MAPPING.default.title;

      const description =
        ERROR_LOGIN_MAPPING[errorCode]?.message ??
        ERROR_LOGIN_MAPPING.default.message;

      showToast({
        type: EToastType.ERROR,
        message: (
          <NotificationContent title={title} description={description} />
        ),
        autoClose: 3000
      });
    }
  });

  const { mutateAsync: getSsoProvider, isPending: isGettingSsoProvider } =
    useGetSsoProviderMutation({
      onSuccess: initSso
    });

  const output = useMemo(
    () => ({
      // State
      isGettingSsoProvider,
      isAuthenticating,
      isAuthenticated: hasLoggedIn && isAuthenticated,
      isLoggingOut,
      isLoggingIn,

      // Actions
      login,
      logout,
      getSsoProvider
    }),
    [
      isGettingSsoProvider,
      hasLoggedIn,
      isAuthenticating,
      isAuthenticated,
      isLoggingOut,
      isLoggingIn,
      login,
      logout,
      getSsoProvider
    ]
  );

  return output;
};
